import Button from 'components/Button';
import FullHeightView from 'components/new/FullHeightView';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { Column, Row } from 'further-ui/components/Layout';
import { revaluation as rules } from 'further-ui/validations';
import { useNotification } from 'hooks/ui/useNotification';
import { ReactNode } from 'react';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { RevaluationExternalState } from '../FormWrapper';
import useRevaluations from '../hooks/useRevaluations';
import HoldingClassRevaluationSection from './HoldingClassRevaluationSection';
import OverallRevaluationSection from './OverallRevaluationSection';
import { HoldingClassInvestmentTotal } from './types';

type RevaluationFormProps = {
  assetName: string;
  assetId: string;
  vehicleId: string;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  onSuccess: (data: rules.RevaluationFormType) => void;
  renderButtons?: (handlers: {
    onSubmit: (data: rules.RevaluationFormType) => void;
    onError: (errors: FieldErrors<rules.RevaluationFormType>) => void;
    handleSubmit: (
      onValid: (data: rules.RevaluationFormType) => void,
      onInvalid?: (errors: FieldErrors<rules.RevaluationFormType>) => void,
    ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  }) => ReactNode;
  viewOnly?: boolean;
};

const RevaluationForm: React.FC<RevaluationFormProps> = ({
  assetName,
  assetId,
  vehicleId,
  holdingClassInvestmentTotals,
  onSuccess,
  renderButtons,
  viewOnly,
}) => {
  const { handleSubmit } = useFormContext<rules.RevaluationFormType>();

  const { formMethods } = useFormWithStateContext<
    rules.RevaluationFormType,
    RevaluationExternalState
  >();

  const { watch, control, getValues } = formMethods;

  const { getAllRevaluations } = useRevaluations({
    watch,
    control,
    getValues,
  });

  const newRevaluations = getAllRevaluations().filter(
    (rev) => rev.isNew && !rev.isRemoved,
  );

  const notification = useNotification();

  const onSubmit = (data: rules.RevaluationFormType) => {
    if (newRevaluations.length === 0) {
      notification.error('No new revaluations found.');
      return;
    }

    onSuccess(data);
  };

  const onError = (errors: FieldErrors<rules.RevaluationFormType>) => {
    notification.error(
      errors.newRevaluations?.root?.message ?? 'Something went wrong.',
    );
  };

  const defaultButtons = (
    <Row>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit(onSubmit, onError)}
      >
        Save and approve
      </Button>
    </Row>
  );

  return (
    <FullHeightView
      actionBar={
        renderButtons
          ? renderButtons({ onSubmit, onError, handleSubmit })
          : defaultButtons
      }
    >
      <Column spacing="lg">
        <OverallRevaluationSection
          assetName={assetName}
          holdingClassInvestmentTotals={holdingClassInvestmentTotals}
          viewOnly={viewOnly}
        />
        <HoldingClassRevaluationSection
          assetId={assetId}
          vehicleId={vehicleId}
          viewOnly={viewOnly}
        />
      </Column>
    </FullHeightView>
  );
};

export default RevaluationForm;
